/**
 * 🎨 DYNAMIC LOYALTY CARD - Carte de fidélité dynamique
 * Accepte un template pour afficher n'importe quel type de carte
 * Supporte le tamponnage avec double-clic + code admin pour annuler
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Wine, Leaf, Cigarette, Gift, CheckCircle2, AlertTriangle,
  Beer, Coffee, Pizza, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import UnstampCodeModal from './UnstampCodeModal';
import { toast } from 'sonner';
import { api } from '../services/api';

// Mapping des icônes disponibles
const ICON_MAP = {
  wine: Wine,
  leaf: Leaf,
  cigarette: Cigarette,
  beer: Beer,
  coffee: Coffee,
  pizza: Pizza,
  gift: Gift,
  sparkles: Sparkles,
};

// Templates par défaut (pour compatibilité avec les anciens types)
const DEFAULT_TEMPLATES = {
  alcohol: {
    id: 'tpl_alcohol',
    name: 'Carte Alcool',
    description: '1 tampon par bouteille achetée',
    reward: 'Bouteille gratuite',
    boxCount: 10,
    color: '#722F37',
    accentColor: '#D4AF37',
    icon: 'wine',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/ssv1zkgy_1.png',
    secondaryColor: '#F3E5E7',
    warning: "L'abus d'alcool est dangereux pour la santé, à consommer avec modération"
  },
  cbd: {
    id: 'tpl_cbd',
    name: 'Carte CBD',
    description: '1 tampon par achat de 2g',
    reward: 'Sachet 2g gratuit',
    boxCount: 10,
    color: '#2D5A45',
    accentColor: '#66BB6A',
    icon: 'leaf',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/5qfu6xxl_5.png',
    secondaryColor: '#E8F5E9',
    warning: 'Le CBD peut altérer la vigilance. Ne conduisez pas après consommation.'
  },
  puffs: {
    id: 'tpl_puffs',
    name: 'Carte Puffs',
    description: '1 tampon par puff acheté',
    reward: 'Puff gratuit par ligne',
    boxCount: 40,
    color: '#6366F1',
    accentColor: '#06B6D4',
    icon: 'cigarette',
    bgImage: 'https://customer-assets.emergentagent.com/job_cardrewards-5/artifacts/f35iwlob_3.png',
    secondaryColor: '#EEF2FF',
    warning: 'La nicotine est fortement addictive. Usage non recommandé aux non-fumeurs.',
    lines: ['20k', '28k', '32k', '36k']
  }
};

export default function DynamicLoyaltyCard({ 
  template,
  card,
  customer,
  onStampChange,
  readOnly = false 
}) {
  const [showUnstampModal, setShowUnstampModal] = useState(false);
  const [pendingUnstampIndex, setPendingUnstampIndex] = useState(null);
  const [clickCount, setClickCount] = useState({});
  const [clickTimeout, setClickTimeout] = useState(null);
  const [isStamping, setIsStamping] = useState(false);

  // Récupérer la config du template (ou utiliser les defaults pour compatibilité)
  const config = template || DEFAULT_TEMPLATES[card?.type] || DEFAULT_TEMPLATES.alcohol;
  const Icon = ICON_MAP[config.icon] || Gift;
  
  // Données de la carte
  const boxes = card?.boxes || Array(config.boxCount).fill(false);
  const stampedCount = boxes.filter(b => b).length;
  const totalBoxes = config.boxCount;
  const isPuffsType = totalBoxes === 40;
  const remainingBoxes = totalBoxes - stampedCount;
  const isNearCompletion = remainingBoxes <= 2 && remainingBoxes > 0;
  
  // Vérifier si la carte est prête à échanger (depuis le backend OU calcul local)
  const isReadyToRedeem = card?.readyToRedeem || (!isPuffsType && stampedCount === totalBoxes);

  // Pour les puffs: vérifier les lignes complètes
  const lines = isPuffsType ? (config.lines || ['Ligne 1', 'Ligne 2', 'Ligne 3', 'Ligne 4']) : [];
  const completedLines = isPuffsType 
    ? lines.map((_, idx) => {
        const lineStart = idx * 10;
        const lineEnd = lineStart + 10;
        return boxes.slice(lineStart, lineEnd).every(b => b);
      })
    : [];
  
  const hasCompletedLine = completedLines.some(completed => completed);
  const isFullyComplete = !isPuffsType && stampedCount === totalBoxes;

  // Couleur secondaire par défaut si non définie
  const secondaryColor = config.secondaryColor || `${config.color}15`;

  // Gérer le clic sur une case
  const handleBoxClick = async (index) => {
    if (readOnly || isStamping) return;

    const isCurrentlyStamped = boxes[index];
    
    if (isCurrentlyStamped) {
      // Double-clic pour décocher
      const currentCount = clickCount[index] || 0;
      
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
      
      if (currentCount === 0) {
        setClickCount({ ...clickCount, [index]: 1 });
        toast.info('🔄 Cliquez à nouveau pour décocher');
        
        const timeout = setTimeout(() => {
          setClickCount({ ...clickCount, [index]: 0 });
        }, 2000);
        setClickTimeout(timeout);
      } else {
        setClickCount({ ...clickCount, [index]: 0 });
        setPendingUnstampIndex(index);
        setShowUnstampModal(true);
      }
    } else {
      // Tamponner directement
      setIsStamping(true);
      try {
        // Déterminer le type de carte pour l'API
        const cardType = getCardTypeForApi();
        await api.stampCard(customer.id, cardType, index, true);
        toast.success('✅ Tampon ajouté !');
        onStampChange?.();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du tamponnage');
      } finally {
        setIsStamping(false);
      }
    }
  };

  // Mapper le template ID vers le type d'API
  const getCardTypeForApi = () => {
    if (config.id === 'tpl_alcohol' || card?.type === 'alcohol') return 'alcool';
    if (config.id === 'tpl_cbd' || card?.type === 'cbd') return 'cbd';
    if (config.id === 'tpl_puffs' || card?.type === 'puffs') return 'puffs';
    // Pour les cartes personnalisées, utiliser l'ID du template
    return config.id || card?.type;
  };

  // Gérer l'annulation avec code admin
  const handleUnstampWithCode = async (code) => {
    try {
      const cardType = getCardTypeForApi();
      await api.stampCard(customer.id, cardType, pendingUnstampIndex, false, code);
      toast.success('✅ Case décochée');
      onStampChange?.();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || '❌ Code invalide');
      return false;
    }
  };

  // Échanger la récompense
  const handleRedeem = async (lineIndex = null) => {
    try {
      const cardType = getCardTypeForApi();
      await api.redeemCard(customer.id, cardType, lineIndex);
      toast.success('🎉 Récompense échangée !');
      onStampChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Rendu de la grille de tampons
  const renderStampGrid = () => {
    if (isPuffsType) {
      return (
        <div className="space-y-4">
          {lines.map((lineName, lineIdx) => {
            const lineStart = lineIdx * 10;
            const lineEnd = lineStart + 10;
            const lineBoxes = boxes.slice(lineStart, lineEnd);
            const lineCompleted = lineBoxes.every(b => b);
            const lineStamped = lineBoxes.filter(b => b).length;
            const lineRemaining = 10 - lineStamped;
            const lineNearCompletion = lineRemaining <= 2 && lineRemaining > 0;

            return (
              <div
                key={lineIdx}
                className={cn(
                  'p-4 rounded-xl border-2',
                  lineNearCompletion && 'animate-pulse-border',
                  lineCompleted && 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500'
                )}
                style={{
                  borderColor: lineCompleted ? '#10B981' : lineNearCompletion ? '#8B5CF6' : '#E2E8F0'
                }}
                data-testid={`puffs-line-${lineIdx}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-sm" style={{ color: config.color }}>
                    {lineName}
                  </span>
                  <span className="text-sm text-gray-500">{lineStamped}/10</span>
                  {lineCompleted && !readOnly && (
                    <Button
                      size="sm"
                      onClick={() => handleRedeem(lineIdx)}
                      className="h-8 px-3 text-xs font-semibold rounded-lg"
                      style={{ backgroundColor: config.accentColor }}
                      data-testid={`redeem-line-${lineIdx}-button`}
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Échanger
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-10 gap-1">
                  {lineBoxes.map((isStamped, boxIdx) => (
                    <button
                      key={boxIdx}
                      onClick={() => handleBoxClick(lineStart + boxIdx)}
                      disabled={readOnly || isStamping}
                      className={cn(
                        'aspect-square rounded-md border-2 flex items-center justify-center min-w-0',
                        !readOnly && 'hover:scale-105 active:scale-95 transition-transform cursor-pointer',
                        readOnly && 'cursor-default',
                        isStamped && 'stamp-animation'
                      )}
                      style={{
                        backgroundColor: isStamped ? config.color : 'white',
                        borderColor: isStamped ? config.color : '#CBD5E1',
                        minWidth: '24px',
                        maxWidth: '40px'
                      }}
                      data-testid={`stamp-box-${lineStart + boxIdx}`}
                    >
                      {isStamped && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Grille standard (10 cases)
    return (
      <div className="grid grid-cols-5 gap-3">
        {boxes.map((isStamped, index) => (
          <button
            key={index}
            onClick={() => handleBoxClick(index)}
            disabled={readOnly || isStamping}
            className={cn(
              'aspect-square rounded-xl border-2 flex items-center justify-center',
              !readOnly && 'hover:scale-110 active:scale-95 cursor-pointer',
              readOnly && 'cursor-default',
              isStamped && 'stamp-animation'
            )}
            style={{
              backgroundColor: isStamped ? config.color : 'white',
              borderColor: isStamped ? config.color : '#CBD5E1'
            }}
            data-testid={`stamp-box-${index}`}
          >
            {isStamped && <CheckCircle2 className="w-6 h-6 text-white" />}
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'relative bg-white rounded-3xl shadow-xl overflow-hidden',
          (isNearCompletion || hasCompletedLine) && 'animate-pulse-border'
        )}
        style={{
          borderWidth: '3px',
          borderStyle: 'solid',
          borderColor: isNearCompletion || hasCompletedLine ? '#8B5CF6' : 'transparent'
        }}
        data-testid={`loyalty-card-${config.id || card?.type}`}
      >
        {/* Header avec image de fond */}
        <div
          className="relative h-36 flex items-center justify-between px-6"
          style={{
            background: config.bgImage 
              ? `linear-gradient(135deg, ${config.color}90 0%, ${config.accentColor}90 100%), url(${config.bgImage})`
              : `linear-gradient(135deg, ${config.color}99 0%, ${config.accentColor}99 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top'
          }}
        >
          <div className="text-white">
            <div className="flex items-center gap-3 mb-2">
              <Icon className="w-8 h-8" />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {config.name}
              </h2>
            </div>
            <p className="text-sm opacity-90">{config.description}</p>
          </div>
          <div className="text-white text-right">
            <div className="text-3xl font-bold">
              {isPuffsType ? `${stampedCount}/40` : `${stampedCount}/${totalBoxes}`}
            </div>
            <div className="text-xs opacity-90">tampons</div>
          </div>
        </div>

        {/* Corps de la carte */}
        <div className="p-6">
          {renderStampGrid()}

          {/* Info récompense */}
          <div
            className={cn(
              "mt-6 p-4 rounded-xl flex items-center justify-between",
              isReadyToRedeem && "ring-2 ring-green-500 animate-pulse"
            )}
            style={{ backgroundColor: isReadyToRedeem ? '#DCFCE7' : secondaryColor }}
          >
            <div className="flex items-center gap-3">
              <Gift className="w-6 h-6" style={{ color: isReadyToRedeem ? '#16A34A' : config.color }} />
              <div>
                <div className="font-semibold" style={{ color: isReadyToRedeem ? '#16A34A' : config.color }}>
                  {isReadyToRedeem ? '🎉 À ÉCHANGER !' : 'Cadeau gratuit'}
                </div>
                <div className="text-sm text-gray-600">{config.reward}</div>
              </div>
            </div>
            {isReadyToRedeem && !readOnly && (
              <Button
                onClick={() => handleRedeem()}
                className="font-semibold bg-green-600 hover:bg-green-700 text-white"
                data-testid="redeem-card-button"
              >
                <Gift className="w-4 h-4 mr-2" />
                Échanger
              </Button>
            )}
          </div>

          {/* Badge progression */}
          {isReadyToRedeem ? (
            <div
              className="mt-4 p-3 rounded-xl text-center font-semibold animate-bounce"
              style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
              data-testid="ready-to-redeem-badge"
            >
              🎁 Carte complète ! Cliquez sur "Échanger" pour obtenir votre récompense
            </div>
          ) : (isNearCompletion || hasCompletedLine) && (
            <div
              className="mt-4 p-3 rounded-xl text-center font-semibold"
              style={{ backgroundColor: '#F3E8FF', color: '#8B5CF6' }}
              data-testid="near-completion-badge"
            >
              {isPuffsType ? '🎉 Ligne complète !' : `🔥 Plus que ${remainingBoxes} tampon${remainingBoxes > 1 ? 's' : ''} !`}
            </div>
          )}

          {/* Avertissement légal */}
          {config.warning && (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 italic px-4">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{config.warning}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Modal pour annulation avec code */}
      <UnstampCodeModal
        isOpen={showUnstampModal}
        onClose={() => {
          setShowUnstampModal(false);
          setPendingUnstampIndex(null);
        }}
        onSubmit={handleUnstampWithCode}
      />
    </>
  );
}

// Export des templates par défaut pour utilisation externe
export { DEFAULT_TEMPLATES, ICON_MAP };
